import moment from 'moment';
import {cloneDeep, mapKeys, isEmpty} from 'lodash';
import initialstate from '../initialstate';
import * as types from '../../actions/actiontypes';
import {
	eyCanvas,
	reviewNoteStatusFilter,
	activityTypeText,
	activityFeedEntities,
	HttpStatusCodes,
	activityFeedQuery
} from '../../util/uiconstants';

export default function activityFeedReducer(state = initialstate.activityFeed, action) {
	switch (action.type) {
		case types.GET_ACTIVITY_FEED_SUMMARY: {
			if (action.responseObj.status === HttpStatusCodes.Ok) {
				let _summary = {...state.summary};
				if (isEmpty(_summary)) {
					_summary = action.response.groupActivitySummary;
				}

				return {
					summary: _summary,
					componentInstructionListAccepted: action.response.componentInstructionListAccepted,
					componentInstructionListReceived: action.response.componentInstructionListReceived,
					documentVersions: action.response.documentVersions,
					primaryInstructionListAccepted: action.response.primaryInstructionListAccepted,
					primaryInstructionListRetured: action.response.primaryInstructionListRetured,
					primaryInstructionListSent: action.response.primaryInstructionListSent,
					primaryLinkRequestListAccepted: normalizeUserData(action.response.primaryLinkRequestListAccepted),
					primaryLinkRequestListSent: normalizeUserData(action.response.primaryLinkRequestListSent),
					primaryLinkRequestListUnlinked: normalizeUserData(action.response.primaryLinkRequestListUnlinked)
				};
			}

			return state;
		}
		case types.RESET_ACTIVITY_FEED_LEFT_COUNT: {
			let _summary = {
				...state.summary
			};

			if (action.engagementType === activityFeedQuery.primary) {
				_summary.primaryInstructionsSent = state.primaryInstructionListSent?.length;
				_summary.primaryInstructionsAccepted = state.primaryInstructionListAccepted?.length;
				_summary.primaryInstructionsRejected = state.primaryInstructionListRetured?.length;
				_summary.primaryLinkRequestsSent = state.primaryLinkRequestListSent?.length;
				_summary.primaryLinkRequestsAccepted = state.primaryLinkRequestListAccepted?.length;
				_summary.primaryLinkRequestsUnlinked = state.primaryLinkRequestListUnlinked?.length;
			} else {
				_summary.componentInstructionsReceived = state.componentInstructionListReceived?.length;
				_summary.componentInstructionsAccepted = state.componentInstructionListAccepted?.length;
			}

			return {
				...state,
				summary: _summary
			};
		}
		case types.CLEAN_ACTIVITY_FEED: {
			return {
				...state,
				summary: state.summary,
				document: null,
				instruction: null,
				linkRequest: null,
				instructionList: state.instructionList,
				linkRequestList: state.linkRequestList,
				activityList: []
			};
		}
		case types.SET_ACTIVITY_FEED_LEFT_COUNT: {
			if (action.count) {
				let summary = {...state.summary};
				summary[action.propName] = action.count;

				return {
					...state,
					summary: summary,
					document: null,
					instruction: null,
					linkRequest: null,
					instructionList: state.instructionList,
					linkRequestList: state.linkRequestList,
					activityList: []
				};
			}

			return state;
		}
		case types.SET_ACTIVITY_FEED_DOCUMENT: {
			return {...state, summary: state.summary, document: action.document};
		}
		case types.SET_ACTIVITY_FEED_INSTRUCTION: {
			return {...state, summary: state.summary, instruction: action.instruction};
		}
		case types.SET_ACTIVITY_FEED_LINK_REQUEST: {
			return {...state, summary: state.summary, linkRequest: action.linkRequest};
		}
		case types.GET_ACTIVITY_FEED_ACTIVITY_LIST: {
			if (!action.response) {
				return state;
			}

			return {
				...state,
				summary: state.summary,
				document: state.document,
				instruction: state.instruction,
				linkRequest: state.linkRequest,
				instructionList: state.instructionList,
				linkRequestList: state.linkRequestList,
				activityList: action.response.activityList.map((item) => {
					const activityInfo = getActivityInfo(item);

					return {
						activityType: item.activityType,
						activityDate: item.activityDate,
						user: getUser(item, activityInfo),
						documentVersion: getDocumentVersion(activityInfo, action.response.documentVersions),
						componentName: activityInfo?.componentname
					};
				})
			};
		}
		case types.POST_RNH_NEW_NOTE: {
			if (action.noteInfo && action.response && state?.activityList) {
				let _activityList = [...state.activityList];

				_activityList.push({
					activityType: activityTypeText.createReviewNote,
					activityDate: action.response.createDate,
					user: action.noteInfo.fromUserName,
					documentVersion: '',
					componentName: ''
				});

				return {
					...state,
					summary: state.summary,
					document: state.document,
					instruction: state.instruction,
					linkRequest: state.linkRequest,
					instructionList: state.instructionList,
					linkRequestList: state.linkRequestList,
					activityList: _activityList
				};
			}

			return state;
		}
		case types.UPDATE_RNH_NOTE_DETAIL: {
			// 4 is the action for update status
			if (action.data && action.data.updateAction === 4 && state?.activityList) {
				let _activityList = [...state.activityList];

				_activityList.push({
					activityType: getReviewNoteStatusType(action.data.annotationStatusId),
					activityDate: moment.utc().toString(),
					user: action.data.statusChangeUserName,
					documentVersion: '',
					componentName: ''
				});

				return {
					...state,
					summary: state.summary,
					document: state.document,
					instruction: state.instruction,
					linkRequest: state.linkRequest,
					instructionList: state.instructionList,
					linkRequestList: state.linkRequestList,
					activityList: _activityList
				};
			}

			return state;
		}
		case types.ADD_APPROVAL_FOR_SEM: {
			if (action.response && state?.activityList) {
				let _activityList = [...state.activityList];

				_activityList.push({
					activityType: activityTypeText.reviewerSignOff,
					activityDate: action.response.createDate,
					user: action.response.name,
					documentVersion: '',
					componentName: ''
				});

				return {
					...state,
					summary: state.summary,
					document: state.document,
					instruction: state.instruction,
					linkRequest: state.linkRequest,
					instructionList: state.instructionList,
					linkRequestList: state.linkRequestList,
					activityList: _activityList
				};
			}

			return state;
		}
		case types.SET_DOCUMENT_LIST_FILTER: {
			if (state.document) {
				return {
					...state,
					document: null
				};
			}

			return state;
		}
		case types.GET_DOCUMENT_LIST: {
			if (action.response) {
				let _activityFeed = cloneDeep(state);
				if (action?.filters?.props?.filteredEntityType && _activityFeed && _activityFeed.summary) {
					if (action.filters.props.filteredEntityType === activityFeedEntities.primaryReceived) {
						_activityFeed.summary.primaryDocumentsReceived = action.response.totalEntityCount;
					} else if (action.filters.props.filteredEntityType === activityFeedEntities.primaryReturned) {
						_activityFeed.summary.primaryDocumentsReturned = action.response.totalEntityCount;
					} else if (action.filters.props.filteredEntityType === activityFeedEntities.componentSent) {
						_activityFeed.summary.componentDocumentsSent = action.response.totalEntityCount;
					} else if (action.filters.props.filteredEntityType === activityFeedEntities.componentReturned) {
						_activityFeed.summary.componentDocumentsReturned = action.response.totalEntityCount;
					} else if (action.filters.props.filteredEntityType === activityFeedEntities.componentResolved) {
						_activityFeed.summary.componentDocumentsFinalized = action.response.totalEntityCount;
					}
				}
				return _activityFeed;
			}
			return state;
		}
		default: {
			return state;
		}
	}
}

function getActivityInfo(activity) {
	try {
		const data = activity.activityData && JSON.parse(activity.activityData);

		return mapKeys(data, (_, key) => key.toLowerCase());
	} catch {
		return null;
	}
}

function getUser(activity, activityInfo) {
	if (activity.userFirstName && activity.userLastName) {
		return `${activity.userFirstName} ${activity.userLastName}`;
	}

	return activityInfo?.username || eyCanvas;
}

function getDocumentVersion(documentInfo, documentVersions) {
	if (documentInfo?.documentversion) {
		if (documentVersions?.some((d) => d.versionNumber == documentInfo.documentversion && !d.isCurrent)) {
			return parseInt(documentInfo.documentversion);
		}
	}

	return undefined;
}

function getReviewNoteStatusType(statusId) {
	let result = '';

	switch (statusId) {
		case reviewNoteStatusFilter.Cleared:
			result = activityTypeText.clearReviewNote;
			break;
		case reviewNoteStatusFilter.Closed:
			result = activityTypeText.closeReviewNote;
			break;
		case reviewNoteStatusFilter.Open:
			result = activityTypeText.openReviewNote;
			break;
	}

	return result;
}

function normalizeUserData(list) {
	let _list = isEmpty(list) ? [] : [...list];

	_list.forEach((x) => {
		let user = x.pointOfContactName || '';

		// clean email
		x.pointOfContactEmail = x.pointOfContactEmail || '';

		// remove user id (US123456)
		user = user.replace(/US+\d+/i, '').trim();

		// from pointOfContactName, take first name [0] and last name [last]
		let names = user.split(' ');
		let lastName = names.pop();
		let firstName = names.join(' ');

		// check if does not have first name
		if (firstName === '') {
			firstName = lastName;
			lastName = '';
		}

		x.pointOfContactFirstName = firstName;
		x.pointOfContactLastName = lastName;

		// full name to use in sort table
		x.pointOfContactFullName = `${user}`.toLowerCase();
	});

	return _list;
}
